import { z } from 'zod';
import { localeSchema, ulidSchema } from './common.js';
import { ROLES } from '../permissions.js';

/** Auth request/response contracts — dev brief §8. */

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address').max(320),
  // No maximum-complexity rules here: length is what matters, and a rule that
  // rejects a good passphrase trains people to pick worse passwords.
  password: z.string().min(8, 'Password must be at least 8 characters').max(1024),
  /** Turnstile token from the login form widget. */
  turnstileToken: z.string().max(4096).optional(),
  /** Extends the refresh cookie to its full 30 days. */
  staySignedIn: z.boolean().default(false),
});
export type LoginRequest = z.infer<typeof loginSchema>;

/**
 * The refresh token travels in an HttpOnly cookie, never in the body — so this
 * endpoint takes nothing. Declared explicitly so the contract is visible.
 */
export const refreshSchema = z.object({}).strict();

export const logoutSchema = z.object({
  /** Revoke every session for this user, not just the current one. */
  allDevices: z.boolean().default(false),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z.string().min(8, 'New password must be at least 8 characters').max(1024),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
  });

export const roleSchema = z.enum(ROLES);

export const createUserSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  fullName: z.string().trim().min(1, 'Name is required').max(200),
  phone: z.string().trim().max(40).optional(),
  role: roleSchema,
  locale: localeSchema.default('lo'),
  /** Required for site-scoped roles; ignored for OWNER and ADMIN. */
  siteIds: z.array(ulidSchema).default([]),
});

/** The authenticated user, as returned by `/auth/login` and `/me`. */
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: (typeof ROLES)[number];
  /** Site ids from `user_site`. Empty for tenant-scoped roles. */
  sites: string[];
  locale: 'lo' | 'en';
  tenantId: string;
}

export interface LoginResponseData {
  token: string;
  /** Access-token lifetime in seconds. 900 = 15 minutes. */
  expiresIn: number;
  user: AuthUser;
}

/** Claims carried in the HS256 access token. Kept small — it is sent per request. */
export interface AccessTokenClaims {
  /** Subject: `app_user.id`. */
  sub: string;
  tid: string;
  role: (typeof ROLES)[number];
  /** Site ids, omitted entirely for tenant-scoped roles to keep the token small. */
  sites?: string[];
  iat: number;
  exp: number;
  /** Token type guard — a refresh token must never be accepted as an access token. */
  typ: 'access';
}
